import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderToolbar,
} from "../../../../_metronic/_partials/controls";
import Table, { dateFormatter } from "../../../components/tables/table";
import { getLogsTasacion, markLogsTasacionAsRead, updateLogTasacion } from "../../../../api/logTasacion";
import {
  Dialog,
  DialogTitle,
  DialogContent,
} from "@material-ui/core";
import VisibilityIcon from "@material-ui/icons/Visibility";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import { Button } from "@material-ui/core";
import { alertError, alertSuccess } from "../../../../utils/logger";

function getData(logs) {
  if (!Array.isArray(logs)) return [];
  return logs.map((log, i) => {
    const r = log.relojInfo || {};
    const t = log.tasacion || {};
    const tAntes = log.tasacionAntesRefinar || {};
    return {
      id: log._id,
      _id: log._id,
      fecha: log.createdAt,
      modeloNombre: log.modeloNombre || "-",
      marca: r.brandName || "-",
      sku: r.sku || "-",
      relojName: r.name || "-",
      min: t.min != null ? t.min : "-",
      avg: t.avg != null ? t.avg : "-",
      max: t.max != null ? t.max : "-",
      minAntes: tAntes.min != null ? tAntes.min : "-",
      avgAntes: tAntes.avg != null ? tAntes.avg : "-",
      maxAntes: tAntes.max != null ? tAntes.max : "-",
      estado: t.estado || "-",
      contenido: t.contenido || "-",
      formularioRellenado: !!log.formularioRellenado,
      refinado: !!log.refinado,
      materialCaja: log.materialCaja || "-",
      esfera: log.esfera || "-",
      diametro: log.diametro || "-",
      contactoNombre: (log.contacto && log.contacto.nombre) || "",
      contactoApellido: (log.contacto && log.contacto.apellido) || "",
      contactoEmail: (log.contacto && log.contacto.email) || "",
      contactoTelefono: (log.contacto && log.contacto.telefono) || "",
      read: !!log.read,
    };
  });
}

const FILTER_OPTIONS = [
  { value: "siempre", label: "Siempre" },
  { value: "1h", label: "Última hora" },
  { value: "24h", label: "Últimas 24 h" },
  { value: "7d", label: "Última semana" },
  { value: "30d", label: "Último mes" },
];

const FORM_FILTER_OPTIONS = [
  { value: "todos", label: "Todos" },
  { value: "con", label: "Solo con formulario rellenado" },
  { value: "sin", label: "Solo sin formulario rellenado" },
];

function filterByPeriod(rows, period) {
  if (!period || period === "siempre") return rows;
  const now = Date.now();
  let since = 0;
  if (period === "1h") since = now - 60 * 60 * 1000;
  else if (period === "24h") since = now - 24 * 60 * 60 * 1000;
  else if (period === "7d") since = now - 7 * 24 * 60 * 60 * 1000;
  else if (period === "30d") since = now - 30 * 24 * 60 * 60 * 1000;
  return rows.filter((row) => {
    const ts = row.fecha ? new Date(row.fecha).getTime() : 0;
    return ts >= since;
  });
}

function filterByForm(rows, formFilter) {
  if (!formFilter || formFilter === "todos") return rows;
  if (formFilter === "con") {
    return rows.filter((row) => row.formularioRellenado);
  }
  if (formFilter === "sin") {
    return rows.filter((row) => !row.formularioRellenado);
  }
  return rows;
}

export default function LogsTasacionPage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsItem, setDetailsItem] = useState(null);
  const [periodFilter, setPeriodFilter] = useState("siempre");
  const [formFilter, setFormFilter] = useState("todos");
  const [markingAllRead, setMarkingAllRead] = useState(false);

  const columns = [
    { dataField: "fecha", text: "Fecha", sort: true, formatter: dateFormatter },
    { dataField: "modeloNombre", text: "Modelo / Reloj", sort: true },
    { dataField: "marca", text: "Marca", sort: true },
    { dataField: "sku", text: "nRef", sort: true },
    { dataField: "min", text: "Min (€)", sort: true },
    { dataField: "avg", text: "Medio (€)", sort: true },
    { dataField: "max", text: "Max (€)", sort: true },
    {
      dataField: "formularioRellenado",
      text: "Form. rellenado",
      sort: true,
      formatter: (cell) => (cell ? "Sí" : "No"),
    },
    {
      dataField: "refinado",
      text: "Refinado",
      sort: true,
      formatter: (cell) => (cell ? "Sí" : "No"),
    },
    {
      dataField: "id",
      text: "",
      formatter: (cell, row) => (
        <Button
          variant="outlined"
          color="primary"
          size="small"
          startIcon={<VisibilityIcon />}
          onClick={async () => {
            setDetailsItem(row);
            setDetailsOpen(true);
            if (row.id || row._id) {
              try {
                await updateLogTasacion(row.id || row._id, { read: true });
                setData((prev) =>
                  prev.map((it) =>
                    (it._id === (row._id || row.id) || it.id === (row.id || row._id))
                      ? { ...it, read: true }
                      : it
                  )
                );
                window.dispatchEvent(new Event("refreshUnreadCounts"));
              } catch (err) {
                console.error("Error marcando log como leído:", err);
              }
            }
          }}
        >
          Ver
        </Button>
      ),
    },
  ];

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getLogsTasacion();
      const list = Array.isArray(res?.data) ? res.data : res?.data?.data ?? [];
      setData(list);
    } catch (error) {
      console.error("Error cargando logs tasación:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const tableData = getData(data);
  const filteredByPeriod = filterByPeriod(tableData, periodFilter);
  const filteredData = filterByForm(filteredByPeriod, formFilter);
  const total = filteredData.length;
  const conFormulario = filteredData.filter((r) => r.formularioRellenado).length;
  const porcentaje = total > 0 ? Math.round((conFormulario / total) * 100) : 0;

  return (
    <>
      <Card>
        <CardHeader title="Logs Tasación">
          <CardHeaderToolbar>
            <Button
              type="button"
              className="btn btn-light-primary btn-sm"
              onClick={async () => {
                try {
                  setMarkingAllRead(true);
                  await markLogsTasacionAsRead();
                  setData((prev) => prev.map((it) => ({ ...it, read: true })));
                  window.dispatchEvent(new Event("refreshUnreadCounts"));
                  alertSuccess({ customMessage: "Todos los logs marcados como leídos." });
                } catch (err) {
                  alertError({ error: err, customMessage: "Error al marcar como leídos." });
                } finally {
                  setMarkingAllRead(false);
                }
              }}
              disabled={markingAllRead}
              startIcon={<DoneAllIcon />}
            >
              {markingAllRead ? "Marcando…" : "Marcar todos como leídos"}
            </Button>
          </CardHeaderToolbar>
        </CardHeader>
        <CardBody>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "16px",
              marginBottom: "16px",
            }}
          >
            <div className="form-group mb-0" style={{ maxWidth: "220px" }}>
              <label>Período</label>
              <select
                className="form-control"
                value={periodFilter}
                onChange={(e) => setPeriodFilter(e.target.value)}
              >
                {FILTER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group mb-0" style={{ maxWidth: "260px" }}>
              <label>Formulario rellenado</label>
              <select
                className="form-control"
                value={formFilter}
                onChange={(e) => setFormFilter(e.target.value)}
              >
                {FORM_FILTER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div
            style={{
              display: "flex",
              gap: "24px",
              flexWrap: "wrap",
              marginBottom: "24px",
              alignItems: "center",
            }}
          >
            <div
              style={{
                padding: "16px 24px",
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
                border: "1px solid #e9ecef",
                minWidth: "160px",
              }}
            >
              <div style={{ fontSize: "0.85rem", color: "#6c757d", marginBottom: "4px" }}>
                Total tasaciones
              </div>
              <div style={{ fontSize: "1.5rem", fontWeight: 600 }}>{total}</div>
            </div>
            <div
              style={{
                padding: "16px 24px",
                backgroundColor: "#e7f5ff",
                borderRadius: "8px",
                border: "1px solid #b3d9f2",
                minWidth: "160px",
              }}
            >
              <div style={{ fontSize: "0.85rem", color: "#0c5460", marginBottom: "4px" }}>
                Con formulario rellenado
              </div>
              <div style={{ fontSize: "1.5rem", fontWeight: 600 }}>{conFormulario}</div>
            </div>
            <div
              style={{
                padding: "16px 24px",
                backgroundColor: "#d4edda",
                borderRadius: "8px",
                border: "1px solid #a3cfbb",
                minWidth: "160px",
              }}
            >
              <div style={{ fontSize: "0.85rem", color: "#155724", marginBottom: "4px" }}>
                % formulario rellenado
              </div>
              <div style={{ fontSize: "1.5rem", fontWeight: 600 }}>{porcentaje}%</div>
            </div>
          </div>

          {loading ? (
            <p>Cargando...</p>
          ) : (
            <Table
              data={filteredData}
              columns={columns}
              rowClasses={(row) => {
                const classes = [];
                if (row.read === false) classes.push("unread-row");
                if (row.formularioRellenado) classes.push("form-filled-row");
                return classes.join(" ");
              }}
            />
          )}
        </CardBody>
      </Card>

      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Detalle log tasación</DialogTitle>
        <DialogContent dividers style={{ padding: 24 }}>
          {detailsItem && (
            <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
              {/* Contacto */}
              <div
                style={{
                  padding: "12px 16px",
                  borderRadius: 8,
                  border: "1px solid #e9ecef",
                  backgroundColor: (detailsItem.contactoNombre || detailsItem.contactoEmail || detailsItem.contactoTelefono) ? "#fdf7e3" : "#f8f9fa",
                }}
              >
                <div
                  style={{
                    fontSize: "0.8rem",
                    color: "#6c757d",
                    fontWeight: 600,
                    marginBottom: 8,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Contacto
                </div>
                {(detailsItem.contactoNombre || detailsItem.contactoEmail || detailsItem.contactoTelefono) ? (
                  <>
                    <p style={{ marginBottom: 4 }}>
                      <strong>Nombre:</strong>{" "}
                      {(detailsItem.contactoNombre || "") + (detailsItem.contactoApellido ? ` ${detailsItem.contactoApellido}` : "")}
                    </p>
                    <p style={{ marginBottom: 4 }}>
                      <strong>Email:</strong> {detailsItem.contactoEmail || "-"}
                    </p>
                    <p style={{ marginBottom: 0 }}>
                      <strong>Teléfono:</strong> {detailsItem.contactoTelefono || "-"}
                    </p>
                  </>
                ) : (
                  <p style={{ marginBottom: 0, color: "#6c757d" }}>Sin datos de contacto.</p>
                )}
              </div>

              {/* Info de reloj */}
              <div
                style={{
                  padding: "12px 16px",
                  borderRadius: 8,
                  border: "1px solid #e9ecef",
                  backgroundColor: "#f8f9fa",
                }}
              >
                <div
                  style={{
                    fontSize: "0.8rem",
                    color: "#6c757d",
                    fontWeight: 600,
                    marginBottom: 8,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Info de reloj
                </div>
                <p style={{ marginBottom: 4 }}>
                  <strong>Fecha:</strong> {detailsItem.fecha ? new Date(detailsItem.fecha).toLocaleString("es-ES") : "-"}
                </p>
                <p style={{ marginBottom: 4 }}>
                  <strong>Modelo / Reloj:</strong> {detailsItem.modeloNombre || "-"}
                </p>
                <p style={{ marginBottom: 4 }}>
                  <strong>Marca:</strong> {detailsItem.marca || "-"}
                </p>
                <p style={{ marginBottom: 4 }}>
                  <strong>nRef:</strong> {detailsItem.sku || "-"}
                </p>
                <p style={{ marginBottom: 4 }}>
                  <strong>Nombre reloj:</strong> {detailsItem.relojName || "-"}
                </p>
                <p style={{ marginBottom: 4 }}>
                  <strong>Estado:</strong> {detailsItem.estado || "-"}
                </p>
                <p style={{ marginBottom: 0 }}>
                  <strong>Contenido:</strong> {detailsItem.contenido || "-"}
                </p>
              </div>

              {/* Resultados tasación */}
              <div
                style={{
                  padding: "12px 16px",
                  borderRadius: 8,
                  border: "1px solid #e9ecef",
                  backgroundColor: "#f8f9fa",
                }}
              >
                <div
                  style={{
                    fontSize: "0.8rem",
                    color: "#6c757d",
                    fontWeight: 600,
                    marginBottom: 10,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Resultados tasación
                </div>

                {(detailsItem.estado || detailsItem.contenido) && (() => {
                  const contenido = (detailsItem.contenido || "").toLowerCase();
                  const tieneEstuche = contenido.includes("estuche");
                  const tieneDocumento = contenido.includes("documento");
                  return (
                    <div style={{ marginBottom: 12, padding: "10px 12px", backgroundColor: "#e9ecef", borderRadius: 8 }}>
                      <div style={{ fontSize: "0.75rem", color: "#6c757d", fontWeight: 600, marginBottom: 6 }}>Criterios de tasación</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "12px 20px", fontSize: "0.9rem" }}>
                        {detailsItem.estado && detailsItem.estado !== "-" && (
                          <span><strong>Usado:</strong> {detailsItem.estado.toLowerCase() === "usado" ? "Sí" : "No"}</span>
                        )}
                        <span><strong>Estuche:</strong> {tieneEstuche ? "Sí" : "No"}</span>
                        <span><strong>Documentos:</strong> {tieneDocumento ? "Sí" : "No"}</span>
                      </div>
                    </div>
                  );
                })()}

                {detailsItem.refinado && (detailsItem.minAntes !== "-" || detailsItem.avgAntes !== "-" || detailsItem.maxAntes !== "-") && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: "0.75rem", color: "#6c757d", fontWeight: 600, marginBottom: 8 }}>Precio antes de refinar</div>
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                      <div style={{ flex: "1 1 80px", minWidth: 70, padding: "10px 12px", borderRadius: 8, backgroundColor: "#e9ecef", textAlign: "center" }}>
                        <div style={{ fontSize: "0.7rem", color: "#6c757d", marginBottom: 2 }}>Mín</div>
                        <div style={{ fontSize: "1rem", fontWeight: 600 }}>{detailsItem.minAntes !== "-" && detailsItem.minAntes != null ? `${Number(detailsItem.minAntes).toLocaleString("es-ES")} €` : "-"}</div>
                      </div>
                      <div style={{ flex: "1 1 80px", minWidth: 70, padding: "10px 12px", borderRadius: 8, backgroundColor: "#e9ecef", textAlign: "center" }}>
                        <div style={{ fontSize: "0.7rem", color: "#6c757d", marginBottom: 2 }}>Medio</div>
                        <div style={{ fontSize: "1rem", fontWeight: 600 }}>{detailsItem.avgAntes !== "-" && detailsItem.avgAntes != null ? `${Number(detailsItem.avgAntes).toLocaleString("es-ES")} €` : "-"}</div>
                      </div>
                      <div style={{ flex: "1 1 80px", minWidth: 70, padding: "10px 12px", borderRadius: 8, backgroundColor: "#e9ecef", textAlign: "center" }}>
                        <div style={{ fontSize: "0.7rem", color: "#6c757d", marginBottom: 2 }}>Máx</div>
                        <div style={{ fontSize: "1rem", fontWeight: 600 }}>{detailsItem.maxAntes !== "-" && detailsItem.maxAntes != null ? `${Number(detailsItem.maxAntes).toLocaleString("es-ES")} €` : "-"}</div>
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ fontSize: "0.75rem", color: "#6c757d", fontWeight: 600, marginBottom: 8 }}>
                  {detailsItem.refinado ? "Precio después de refinar" : "Precio"}
                </div>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
                  <div
                    style={{
                      flex: "1 1 100px",
                      minWidth: 90,
                      padding: "14px 16px",
                      borderRadius: 10,
                      backgroundColor: "#f0f4f8",
                      border: "1px solid #d4e1ed",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: "0.75rem", color: "#5a6c7d", marginBottom: 4, fontWeight: 600 }}>Mínimo</div>
                    <div style={{ fontSize: "1.25rem", fontWeight: 700, color: "#2c5282" }}>
                      {detailsItem.min !== "-" && detailsItem.min != null ? `${Number(detailsItem.min).toLocaleString("es-ES")} €` : "-"}
                    </div>
                  </div>
                  <div
                    style={{
                      flex: "1 1 100px",
                      minWidth: 90,
                      padding: "14px 16px",
                      borderRadius: 10,
                      backgroundColor: "#e8f4ea",
                      border: "2px solid #4caf50",
                      textAlign: "center",
                      boxShadow: "0 2px 8px rgba(76, 175, 80, 0.15)",
                    }}
                  >
                    <div style={{ fontSize: "0.75rem", color: "#2e7d32", marginBottom: 4, fontWeight: 600 }}>Medio</div>
                    <div style={{ fontSize: "1.35rem", fontWeight: 700, color: "#1b5e20" }}>
                      {detailsItem.avg !== "-" && detailsItem.avg != null ? `${Number(detailsItem.avg).toLocaleString("es-ES")} €` : "-"}
                    </div>
                  </div>
                  <div
                    style={{
                      flex: "1 1 100px",
                      minWidth: 90,
                      padding: "14px 16px",
                      borderRadius: 10,
                      backgroundColor: "#fef3e8",
                      border: "1px solid #f5d0a9",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: "0.75rem", color: "#8d5a00", marginBottom: 4, fontWeight: 600 }}>Máximo</div>
                    <div style={{ fontSize: "1.25rem", fontWeight: 700, color: "#e65100" }}>
                      {detailsItem.max !== "-" && detailsItem.max != null ? `${Number(detailsItem.max).toLocaleString("es-ES")} €` : "-"}
                    </div>
                  </div>
                </div>

                <p style={{ marginBottom: 4 }}>
                  <strong>Refinado:</strong> {detailsItem.refinado ? "Sí" : "No"}
                </p>
                {detailsItem.refinado && (detailsItem.materialCaja !== "-" || detailsItem.esfera !== "-" || detailsItem.diametro !== "-") && (
                  <div
                    style={{
                      marginTop: 8,
                      padding: "12px 16px",
                      backgroundColor: "#ffffff",
                      borderRadius: 8,
                      border: "1px solid #e9ecef",
                    }}
                  >
                    <div style={{ fontSize: "0.8rem", color: "#6c757d", fontWeight: 600, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Filtros de refinamiento
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      {detailsItem.materialCaja !== "-" && <p style={{ margin: 0 }}><strong>Material de la caja:</strong> {detailsItem.materialCaja}</p>}
                      {detailsItem.esfera !== "-" && <p style={{ margin: 0 }}><strong>Esfera:</strong> {detailsItem.esfera}</p>}
                      {detailsItem.diametro !== "-" && <p style={{ margin: 0 }}><strong>Diámetro:</strong> {detailsItem.diametro}</p>}
                    </div>
                  </div>
                )}
              </div>

              <p style={{ margin: 0 }}>
                <strong>Formulario rellenado:</strong> {detailsItem.formularioRellenado ? "Sí" : "No"}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
